import * as React from 'react';
import { useDefaultLayout } from 'react-resizable-panels';

import { cn } from '../lib/utils';
import { useMediaQuery } from '../hooks/use-media-query';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '../primitives/drawer';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../primitives/resizable';
import {
  readPersistedSelection,
  writePersistedSelection,
} from './master-detail-selection';

export interface UseMasterDetailSelectionOpts {
  /**
   * Persist the selected id under `tyndo.mds.<key>` so reloading the view
   * restores the operator's place. Omit for ephemeral selection.
   */
  persistKey?: string;
}

export interface UseMasterDetailSelectionResult {
  /** Currently selected record id, or null. */
  selectedId: string | null;
  /** Select a record (opens the detail pane / mobile sheet). */
  select: (id: string) => void;
  /** Clear the selection (closes the mobile sheet). */
  clear: () => void;
  /** True when a record is selected — feed `ResponsiveDetailMode.detailOpen`. */
  detailOpen: boolean;
  /** Sheet open-change handler — feed `ResponsiveDetailMode.onDetailOpenChange`. */
  onDetailOpenChange: (open: boolean) => void;
}

/**
 * Master-detail selection state, extracted once instead of re-implemented
 * per consumer. Pairs with {@link ResponsiveDetailMode}:
 *
 * @example
 * const selection = useMasterDetailSelection({ persistKey: 'alarms-console' });
 * <ResponsiveDetailMode
 *   detailOpen={selection.detailOpen}
 *   onDetailOpenChange={selection.onDetailOpenChange}
 *   master={<AlarmList onSelect={(a) => selection.select(a.id)} />}
 *   detail={selection.selectedId ? <AlarmDetail id={selection.selectedId} /> : <EmptyState title="Select an alarm" />}
 * />
 */
export function useMasterDetailSelection(
  opts?: UseMasterDetailSelectionOpts,
): UseMasterDetailSelectionResult {
  const persistKey = opts?.persistKey;
  const [selectedId, setSelectedId] = React.useState<string | null>(() => {
    if (!persistKey || typeof window === 'undefined') return null;
    return readPersistedSelection(window.localStorage, persistKey);
  });

  React.useEffect(() => {
    if (!persistKey || typeof window === 'undefined') return;
    writePersistedSelection(window.localStorage, persistKey, selectedId);
  }, [persistKey, selectedId]);

  const select = React.useCallback((id: string) => setSelectedId(id), []);
  const clear = React.useCallback(() => setSelectedId(null), []);
  const onDetailOpenChange = React.useCallback((open: boolean) => {
    if (!open) setSelectedId(null);
  }, []);

  return {
    selectedId,
    select,
    clear,
    detailOpen: selectedId != null,
    onDetailOpenChange,
  };
}

export interface ResponsiveDetailModeProps {
  /** Master pane — the list. Always visible. */
  master: React.ReactNode;
  /** Detail pane — side panel on >= md, bottom sheet below. */
  detail: React.ReactNode;
  /** Whether the detail surface is open (drives the mobile sheet). */
  detailOpen: boolean;
  /** Called when the mobile sheet is dismissed (swipe-down / overlay tap). */
  onDetailOpenChange?: (open: boolean) => void;
  /** Sheet header title (a11y-required on mobile). */
  detailTitle?: string;
  /** Optional sheet header description. */
  detailDescription?: string;
  /** Initial detail-panel share (percent) on >= md. Default 40. */
  defaultDetailSize?: number;
  /** Min/max detail-panel share (percent) on >= md. */
  minDetailSize?: number;
  maxDetailSize?: number;
  /**
   * Persist the desktop panel split. Becomes the react-resizable-panels
   * `autoSaveId`, so the divider position survives reloads.
   */
  storageKey?: string;
  className?: string;
}

/**
 * ResponsiveDetailMode — the master-detail archetype.
 *
 * >= md: a horizontal ResizablePanelGroup (drag the divider; split persists
 * via `storageKey`). Below md: the master takes the full width and the
 * detail renders in a vaul bottom sheet that opens at ~60% of the viewport
 * and drag-expands to full height.
 */
export function ResponsiveDetailMode({
  master,
  detail,
  detailOpen,
  onDetailOpenChange,
  detailTitle = 'Detail',
  detailDescription,
  defaultDetailSize = 40,
  minDetailSize = 25,
  maxDetailSize = 70,
  storageKey,
  className,
}: ResponsiveDetailModeProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)', { defaultValue: true });
  const [snapPoint, setSnapPoint] = React.useState<number | string | null>(0.6);

  // Reopen at the 60% snap each time a new selection opens the sheet.
  React.useEffect(() => {
    if (detailOpen) setSnapPoint(0.6);
  }, [detailOpen]);

  // Persist the desktop split (flex-grow layout map) under tyndo.rdm.<key>.
  // The hook must run unconditionally; its result is only spread onto the
  // Group when the caller opted into persistence via `storageKey`.
  const fallbackLayoutId = React.useId();
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: storageKey ? `tyndo.rdm.${storageKey}` : fallbackLayoutId,
    panelIds: ['master', 'detail'],
  });

  if (isDesktop) {
    return (
      <ResizablePanelGroup
        orientation="horizontal"
        defaultLayout={storageKey ? defaultLayout : undefined}
        onLayoutChanged={storageKey ? onLayoutChanged : undefined}
        className={cn('h-full min-h-0 rounded-md border border-border/60', className)}
        data-testid="responsive-detail-desktop"
      >
        <ResizablePanel
          id="master"
          defaultSize={`${100 - defaultDetailSize}%`}
          minSize={`${100 - maxDetailSize}%`}
        >
          <div className="h-full min-h-0 overflow-y-auto">{master}</div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel
          id="detail"
          defaultSize={`${defaultDetailSize}%`}
          minSize={`${minDetailSize}%`}
          maxSize={`${maxDetailSize}%`}
        >
          <div className="h-full min-h-0 overflow-y-auto bg-surface-raised">
            {detail}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    );
  }

  return (
    <div className={cn('flex min-h-0 flex-col', className)} data-testid="responsive-detail-mobile">
      {master}
      <Drawer
        open={detailOpen}
        onOpenChange={onDetailOpenChange}
        snapPoints={[0.6, 1]}
        activeSnapPoint={snapPoint}
        setActiveSnapPoint={setSnapPoint}
      >
        <DrawerContent className="h-full">
          <DrawerHeader>
            <DrawerTitle>{detailTitle}</DrawerTitle>
            {detailDescription && (
              <DrawerDescription>{detailDescription}</DrawerDescription>
            )}
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {detail}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

ResponsiveDetailMode.displayName = 'ResponsiveDetailMode';
